<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import TimesIcon from '@primeicons/vue/times';
import { button as greenButtonTheme } from '@apparatix/theme/Green';

export interface ModalProps {
  visible: boolean;
  title: string;
  subtitle?: string;
  position?: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright' | undefined;
  /**
   * height should be provided as CSS classes, NOT pixel/rem values or raw numbers
   */
  height?: string;
  /**
   * width should be provided as CSS classes, NOT pixel/rem values or raw numbers
   */
  width?: string;
  cancelLabel?: string;
  acceptLabel?: string;
  disableCancel?: boolean;
  disableAccept?: boolean;
  disableFooter?: boolean;
  canClickAccept?: boolean;
  canClickCancel?: boolean;
  closeOnMask?: boolean;
  headerColor?: string;
}

const props = withDefaults(defineProps<ModalProps>(), {
  subtitle: '',
  position: 'top',
  height: 'h-screen lg:h-[95vh] xl:h-3/4',
  width: 'w-screen lg:w-[95vw]  xl:w-3/5',
  cancelLabel: 'Cancel',
  acceptLabel: 'Ok',
  disableAccept: false,
  disableCancel: false,
  disableFooter: false,
  canClickAccept: true,
  canClickCancel: true,
  closeOnMask: false,
});

const layoutClasses = computed(() => `!mt-0 ${props.height} ${props.width}`);

const headerStyle = computed(() => props.headerColor
  ? { background: props.headerColor }
  : {});


const emit = defineEmits<{
  (e: 'accept'): void;
  (e: 'close'): void;
}>();

const onCancel = () => emit('close');
const onAccept = () => emit('accept');
</script>

<template>
  <Dialog
    :visible="visible"
    :position="position"
    :dismissable-mask="closeOnMask"
    modal
    :pt="{
      root: {
        style: {
          boxShadow: 'none',
          border: 'none',
          maxHeight: '100%',
        },
        class: layoutClasses
      }
    }"
    @update:visible="(v) => { if (!v) emit('close') }"
  >
    <template #container>
      <div class="flex flex-col h-full w-full">
        <div
          class="w-full"
          :class="$style['header']"
          :style="headerStyle"
        >
          <slot name="header">
            <div class="flex items-center p-4 text-[var(--p-text-color)]">
              <div class="flex flex-col grow gap-2">
                <h1 class="text-base font-medium leading-none">
                  {{ title }}
                </h1>
                <p v-if="subtitle" class="leading-none text-sm">
                  {{ subtitle }}
                </p>
              </div>
              <div class="flex-none cursor-pointer" @click="emit('close')">
                <TimesIcon />
              </div>
            </div>
          </slot>
        </div>
        <div class="grow w-full overflow-y-auto">
          <slot name="body" />
        </div>
        <div v-if="!disableFooter" class="flex-none w-full lg:rounded-b-lg bg-[var(--white)] border-solid border-t border-[var(--secondary-border-color)]">
          <slot name="footer">
            <div class="w-full flex justify-end items-center gap-2 p-4">
              <Button
                v-if="!disableCancel"
                :disabled="!canClickCancel"
                severity="secondary"
                @click="onCancel"
              >
                {{ cancelLabel }}
              </Button>
              <Button
                v-if="!disableAccept"
                :disabled="!canClickAccept"
                :dt="greenButtonTheme"
                @click="onAccept"
              >
                {{ acceptLabel }}
              </Button>
            </div>
          </slot>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<style module>
  .header {
    border-bottom: 1px solid var(--p-surface-200, #E2E8F0);
    box-shadow: 0px 1px 2px 0px rgba(18, 18, 23, 0.05);
    background: #fff;
  }
</style>
